import React, { useContext, useMemo } from "react";
import { Button, Drawer, Input, Tag } from "antd";

import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import AppContext from "../../../context/AppContext";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { DUPLICATE_IDENTITY_REVIEW_PERMISSIONS } from "../../../utils/constants/permissionsConstants";
import { buildUserManagerUrl } from "../../../utils/NavigationUtils";
import {
  classificationLabel,
  dupTypeLabel,
  statusLabel,
} from "./DuplicateIdentityGroupsTable";
import { useDuplicateIdentityReview } from "./DuplicateIdentityReviewContext";
import styles from "./stylesDuplicateIdentityReview.module.css";

type AppContextShape = {
  adminPermissions?: string[];
};

const DuplicateIdentityDetailDrawer: React.FC = () => {
  const { adminPermissions = [] } = useContext(AppContext) as AppContextShape;
  const {
    selectedGroupId,
    closeDetail,
    detailQuery,
    notesDraft,
    setNotesDraft,
    handleMarkSupportContacted,
    handleEscalate,
    handleIgnore,
    handleMergeDryRun,
    isUpdating,
    isDryRunPending,
    mergeDryRunCorrelationId,
  } = useDuplicateIdentityReview();

  const canResolve = adminPermissions.includes(
    DUPLICATE_IDENTITY_REVIEW_PERMISSIONS.RESOLVE,
  );
  const canTriggerMerge = adminPermissions.includes(
    DUPLICATE_IDENTITY_REVIEW_PERMISSIONS.TRIGGER_MERGE,
  );

  const { data, isLoading, isError } = detailQuery;
  const open = Boolean(selectedGroupId);

  const showMergeDryRun = useMemo(
    () => data?.classification === "merge_candidate",
    [data?.classification],
  );

  return (
    <Drawer
      open={open}
      onClose={closeDetail}
      width={520}
      title={AppStrings.KYC_DUPLICATE_IDENTITY_REVIEW_TITLE}
      destroyOnClose
      data-testid="duplicate-identity-detail-drawer"
    >
      {isLoading && <RebetLoader height="32px" width="32px" />}
      {isError && (
        <div
          className={`${styles.stateMessage} ${styles.stateMessageError}`}
          data-testid="duplicate-identity-detail-error"
        >
          {AppStrings.KYC_DUPLICATE_IDENTITY_DETAIL_LOAD_ERROR}
        </div>
      )}
      {!isLoading && !isError && data && (
        <>
          <section className={styles.detailSection}>
            <h4 className={styles.detailSectionTitle}>
              {AppStrings.KYC_DUPLICATE_IDENTITY_DUP_TYPE}
            </h4>
            <Tag>{dupTypeLabel(data.dup_type)}</Tag>
          </section>

          <section className={styles.detailSection}>
            <h4 className={styles.detailSectionTitle}>
              {AppStrings.KYC_DUPLICATE_IDENTITY_CLASSIFICATION}
            </h4>
            <Tag color="blue">{classificationLabel(data.classification)}</Tag>
          </section>

          <section className={styles.detailSection}>
            <h4 className={styles.detailSectionTitle}>
              {AppStrings.KYC_DUPLICATE_IDENTITY_STATUS}
            </h4>
            <Tag>{statusLabel(data.status)}</Tag>
          </section>

          <section className={styles.detailSection}>
            <h4 className={styles.detailSectionTitle}>
              {AppStrings.KYC_DUPLICATE_IDENTITY_MEMBER_IDS}
            </h4>
            <ul className={styles.memberList}>
              {data.user_ids.map((userId) => (
                <li key={userId}>
                  <a
                    href={buildUserManagerUrl(userId)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {userId}
                  </a>
                </li>
              ))}
            </ul>
          </section>

          {data.survivor_user_id ? (
            <section className={styles.detailSection}>
              <h4 className={styles.detailSectionTitle}>
                {AppStrings.KYC_DUPLICATE_IDENTITY_PROPOSED_SURVIVOR}
              </h4>
              <a
                href={buildUserManagerUrl(data.survivor_user_id)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {data.survivor_user_id}
              </a>
              <p className={styles.detailHint}>
                {AppStrings.KYC_DUPLICATE_IDENTITY_PROPOSED_SURVIVOR_HINT}
              </p>
            </section>
          ) : null}

          <section className={styles.detailSection}>
            <h4 className={styles.detailSectionTitle}>
              {AppStrings.KYC_DUPLICATE_IDENTITY_NEVER_CLOBBER_TITLE}
            </h4>
            <p className={styles.detailHint}>
              {AppStrings.KYC_DUPLICATE_IDENTITY_NEVER_CLOBBER_BODY}
            </p>
          </section>

          <section className={styles.detailSection}>
            <h4 className={styles.detailSectionTitle}>
              {AppStrings.KYC_DUPLICATE_IDENTITY_NOTES}
            </h4>
            <Input.TextArea
              data-testid="duplicate-identity-notes"
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              rows={3}
              disabled={!canResolve}
            />
          </section>

          {canResolve && (
            <div className={styles.detailActions}>
              <Button
                data-testid="duplicate-identity-mark-support-contacted"
                loading={isUpdating}
                onClick={() => void handleMarkSupportContacted()}
              >
                {AppStrings.KYC_DUPLICATE_IDENTITY_MARK_SUPPORT_CONTACTED}
              </Button>
              <Button
                data-testid="duplicate-identity-escalate"
                loading={isUpdating}
                onClick={() => void handleEscalate()}
              >
                {AppStrings.KYC_DUPLICATE_IDENTITY_ESCALATE}
              </Button>
              <Button
                data-testid="duplicate-identity-ignore"
                loading={isUpdating}
                onClick={() => void handleIgnore()}
              >
                {AppStrings.KYC_DUPLICATE_IDENTITY_IGNORE}
              </Button>
            </div>
          )}

          {canTriggerMerge && showMergeDryRun && (
            <div className={styles.detailActions}>
              <Button
                type="primary"
                data-testid="duplicate-identity-merge-dry-run"
                loading={isDryRunPending}
                onClick={() => void handleMergeDryRun()}
              >
                {AppStrings.KYC_DUPLICATE_IDENTITY_RUN_MERGE_DRY_RUN}
              </Button>
            </div>
          )}

          {mergeDryRunCorrelationId ? (
            <div
              className={styles.correlationBanner}
              data-testid="duplicate-identity-correlation-banner"
            >
              <strong>{AppStrings.KYC_DUPLICATE_IDENTITY_CORRELATION_ID}:</strong>{" "}
              <code>{mergeDryRunCorrelationId}</code>
              <p className={styles.detailHint}>
                {AppStrings.KYC_DUPLICATE_IDENTITY_CHECK_BACK_LATER}
              </p>
            </div>
          ) : null}
        </>
      )}
    </Drawer>
  );
};

export default DuplicateIdentityDetailDrawer;
